import {http} from '../http'
import * as coreStaff from '../../coreApi/staff'
import * as corePersons from '../../coreApi/persons'
import {APIResponse} from '../types'
import {Client} from '../../../types'
import {handleError} from '../handleError'
import {RawPersonName} from '../../coreApi/persons/getNames'

const STATUS_ACTIVE = 1
const STAFF_PAGE_SIZE = 100
// Max person IDs per GetNames request. GetNames isn't paginated; this bounds the
// URL length (one `id=` query param per ID), not a page count.
const NAMES_BATCH_SIZE = 100

export interface HiredStaffMember {
  ID: string
  name: {
    first: string
    last: string
    preferred: string | null
  }
  fullName: string
  // Always null for now: the roster is resolved via persons GetNames, which
  // returns no contact details (see fetchNamesByIDs). Kept on the shape so
  // email can be reintroduced later without churning callers.
  email: string | null
  years: number | null
}

interface Params {
  clientID: Client['ID']
  seasonID: number
}

type FetchHiredStaffReturn = APIResponse<HiredStaffMember[]>

interface HiredStaffIndex {
  personIDs: number[]
  // PersonID (string-keyed) → Years from the /v0/staff row. Years isn't
  // carried by persons GetNames, so we capture it here and re-join after the
  // names fetch. String keys so the lookup tolerates either a number or string
  // PersonID across prod/test.
  yearsByPersonID: Map<string, number | null>
}

const fetchAllHiredPersonIDs = async (
  clientID: Client['ID'],
  seasonID: number,
): Promise<HiredStaffIndex> => {
  const personIDs: number[] = []
  const yearsByPersonID = new Map<string, number | null>()
  let pageNumber = 1
  let hasNext = true

  while (hasNext) {
    // eslint-disable-next-line no-await-in-loop
    const response = await coreStaff.list(http, {
      ClientID: Number(clientID),
      SeasonID: seasonID,
      Status: STATUS_ACTIVE,
      PageNumber: pageNumber,
      PageSize: STAFF_PAGE_SIZE,
    })
    response.data.Results.forEach((r) => {
      personIDs.push(r.PersonID)
      yearsByPersonID.set(r.PersonID.toString(), r.Years ?? null)
    })
    hasNext = Boolean(response.data.Next)
    pageNumber += 1
  }

  return {personIDs, yearsByPersonID}
}

const fetchNamesByIDs = async (
  clientID: Client['ID'],
  ids: number[],
): Promise<RawPersonName[]> => {
  if (ids.length === 0) return []

  const batches: number[][] = []
  for (let i = 0; i < ids.length; i += NAMES_BATCH_SIZE) {
    batches.push(ids.slice(i, i + NAMES_BATCH_SIZE))
  }

  // Resolve names through persons GetNames rather than the full persons List:
  // GetNames is client-scoped only (no per-person IDOR check), so a camp admin
  // can read staff names for their own client without the all-person role that
  // List requires. It returns no contact details, hence no email this revision.
  // Each batch is independent, so fire them in parallel; batch size is bounded
  // to keep the URL short (one `id=` query param per ID).
  const responses = await Promise.all(
    batches.map((batch) =>
      corePersons.getNames(http, {
        ClientID: Number(clientID),
        IDs: batch,
      }),
    ),
  )

  return responses.flatMap((response) => response.data)
}

const toHiredStaffMember = (
  person: RawPersonName,
  years: number | null,
): HiredStaffMember => {
  const first = person.First ?? ''
  const last = person.Last ?? ''
  const preferred = person.Preferred?.trim() || null
  // When a preferred name is present, show it in quotes between first and last,
  // e.g. Robert "Bobby" Tables.
  const namePieces = preferred ? [first, `"${preferred}"`, last] : [first, last]
  return {
    ID: person.PersonID.toString(),
    name: {first, last, preferred},
    fullName: namePieces.filter(Boolean).join(' '),
    email: null,
    years,
  }
}

const ERROR_PATH = 'api.staff.fetchHiredStaff()'
const GENERIC_ERROR =
  'Something went wrong while loading staff. Please try again.'

export const fetchHiredStaff = async ({
  clientID,
  seasonID,
}: Params): Promise<FetchHiredStaffReturn> => {
  try {
    const {personIDs, yearsByPersonID} = await fetchAllHiredPersonIDs(
      clientID,
      seasonID,
    )
    const persons = await fetchNamesByIDs(clientID, personIDs)
    const members = persons
      .map((p) =>
        toHiredStaffMember(
          p,
          yearsByPersonID.get(p.PersonID.toString()) ?? null,
        ),
      )
      .sort((a, b) => a.fullName.localeCompare(b.fullName))
    return {result: 'success', data: members}
  } catch (e) {
    return handleError(ERROR_PATH, GENERIC_ERROR, e)
  }
}
